/* eslint-disable @typescript-eslint/naming-convention */
import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import {
    CHART_CONFIG,
    ChartDataPoint,
    Frame,
    MarkerAnnotation,
    MeasurementData,
    ResponseGraphData,
    TrendGraphData
} from '../models/measurement-view.types';

@Injectable({
    providedIn: 'root'
})
export class MeasurementDataService {
    private currentMeasurementSubject = new BehaviorSubject<MeasurementData | null>(null);
    private selectedFrameSubject = new BehaviorSubject<Frame | null>(null);
    private hoveredWaveIndexSubject = new BehaviorSubject<number | null>(null);

    public currentMeasurement$ = this.currentMeasurementSubject.asObservable();
    public selectedFrame$ = this.selectedFrameSubject.asObservable();
    public hoveredWaveIndex$ = this.hoveredWaveIndexSubject.asObservable();

    public loadMeasurementData(data: MeasurementData): void {
        this.selectedFrameSubject.next(null);
        this.hoveredWaveIndexSubject.next(null);
        this.currentMeasurementSubject.next(data);
    }

    public selectFrame(frame: Frame): void {
        this.selectedFrameSubject.next(frame);
        this.hoveredWaveIndexSubject.next(null);
    }

    public setHoveredWaveIndex(index: number | null): void {
        this.hoveredWaveIndexSubject.next(index);
    }

    public getCurrentMeasurement(): MeasurementData | null {
        return this.currentMeasurementSubject.value;
    }

    public getSelectedFrame(): Frame | null {
        return this.selectedFrameSubject.value;
    }

    /**
     * Update marker annotations and notify all subscribers
     */
    public updateMarkerAnnotations(annotations: MarkerAnnotation[]): void {
        const currentMeasurement = this.currentMeasurementSubject.value;
        if (currentMeasurement) {
            currentMeasurement.dynamicData.marker_annotations = annotations;
            // Emit a new reference to trigger subscribers
            this.currentMeasurementSubject.next({ ...currentMeasurement });
        }
    }

    /**
     * Convert measurement data to trend graph format
     */
    public getTrendGraphData(measurement: MeasurementData, useRelativeTime: boolean = false): TrendGraphData {
        const frames = measurement.data.filter((entry): entry is Frame => entry.type === 'frame');
        const compoundFrames = frames.filter((frame) => this.isCompoundFrame(frame));
        const startDate = new Date(`${measurement.meta.date}T${measurement.meta.time}`);

        const tofr: ChartDataPoint[] = [];
        const tofc: ChartDataPoint[] = [];
        const ptc: ChartDataPoint[] = [];
        const amplitude: ChartDataPoint[] = [];
        const st: ChartDataPoint[] = [];

        // Process compound frames (TOFR, TOFC, PTC measurements)
        compoundFrames.forEach((frame) => {
            const time: number = useRelativeTime ? frame.time : startDate.getTime() + frame.time * 1000;
            const point = { x: time, y: frame.value };

            switch (frame.frame_type) {
                case 'TOFR':
                case 'PTC-TOFR':
                    tofr.push(point);
                    break;
                case 'TOFC':
                case 'PTC-TOFC':
                    tofc.push(point);
                    break;
                case 'PTC':
                    ptc.push(point);
                    break;
                case 'ST':
                case 'PTC-ST':
                    break;
                case 'CAL': {
                    throw new Error('Not implemented yet: "CAL" case');
                }
                case 'CAL_UNPROCESSED': {
                    throw new Error('Not implemented yet: "CAL_UNPROCESSED" case');
                }
            }
        });

        // Process all frames for amplitude data (exclude CAL/PTC-ST modes)
        frames
            .filter((f) => f.frame_type !== 'PTC-ST' && f.frame_type !== 'CAL' && f.frame_type !== 'CAL_UNPROCESSED')
            .forEach((frame) => {
                const time: number = useRelativeTime ? frame.time : startDate.getTime() + frame.time * 1000;
                amplitude.push({
                    x: time,
                    y: frame.wave_data.amplitude
                });
            });

        // Collect ST t1Tref values for plotting in the TOFR (0-110%) section
        frames
            .filter((f) => f.frame_type === 'ST' && f.t1Tref != null /*&& f.t1Tref > 0*/)
            .forEach((frame) => {
                const time: number = useRelativeTime ? frame.time : startDate.getTime() + frame.time * 1000;
                st.push({ x: time, y: frame.t1Tref });
            });

        return { tofr, tofc, ptc, amplitude, st };
    }

    /**
     * Get response graph data for selected frame
     */
    public getResponseGraphData(frame: Frame): ResponseGraphData | null {
        if (!frame) return null;

        return {
            waves: frame.wave_data.waves,
            frameType: frame.frame_type,
            maxAmplitude: Math.max(
                ...frame.wave_data.waves.map((w) => Math.max(w.peak_value, Math.abs(w.valley_value)))
            ),
            minAmplitude: Math.min(
                ...frame.wave_data.waves.map((w) => Math.min(w.valley_value, -Math.abs(w.peak_value)))
            )
        };
    }

    /**
     * Get maximum amplitude from all frames
     */
    public getMaxAmplitude(frames: Frame[]): number {
        return frames.reduce((max, frame) => {
            return Math.max(max, frame.wave_data.amplitude);
        }, -1);
    }
    /**
     * Check if frame is compound (TOF/PTC measurement)
     */
    private isCompoundFrame(frame: Frame): boolean {
        return (
            frame.frame_type === 'PTC' ||
            frame.frame_type === 'TOFR' ||
            frame.frame_type === 'TOFC' ||
            frame.frame_type === 'PTC-TOFR' ||
            frame.frame_type === 'PTC-TOFC'
        );
    }

    /**
     * Calculate TOFR y-scale position (matching original implementation)
     */
    public getTofrDataSeriesValue(value: number): number {
        const g_trend_ptc_area_height_pct = CHART_CONFIG.TREND_PTC_AREA_HEIGHT_PCT;
        const g_trend_tofc_area_height_pct = CHART_CONFIG.TREND_TOFC_AREA_HEIGHT_PCT;
        const g_trend_tofr_area_start_pct = g_trend_ptc_area_height_pct + g_trend_tofc_area_height_pct;
        const g_trend_tofr_area_height_pct = 100 - g_trend_tofr_area_start_pct;
        const g_max_tofr_percentage = CHART_CONFIG.MAX_TOFR_PERCENTAGE;

        return (
            g_trend_tofr_area_start_pct +
            Math.round((g_trend_tofr_area_height_pct * Math.min(value, g_max_tofr_percentage)) / g_max_tofr_percentage)
        );
    }

    /**
     * Calculate TOFC y-scale position (matching original implementation)
     */
    public getTofcDataSeriesValue(value: number): number {
        const g_trend_ptc_area_height_pct = CHART_CONFIG.TREND_PTC_AREA_HEIGHT_PCT;
        const g_trend_tofc_area_height_pct = CHART_CONFIG.TREND_TOFC_AREA_HEIGHT_PCT;

        return g_trend_ptc_area_height_pct + Math.round((g_trend_tofc_area_height_pct * value) / 4);
    }

    /**
     * Calculate PTC y-scale position (matching original implementation)
     */
    public getPtcDataSeriesValue(value: number): number {
        const g_trend_ptc_area_height_pct = CHART_CONFIG.TREND_PTC_AREA_HEIGHT_PCT;
        return g_trend_ptc_area_height_pct * (value / 20);
    }

    /**
     * Round number to specified decimal places
     */
    public roundValue(value: number, decimals: number = 2): number {
        return Math.round(value * Math.pow(10, decimals)) / Math.pow(10, decimals);
    }

    /**
     * Format seconds to HH:mm:ss format
     */
    public formatSecondsToTime(seconds: number, precision: number = 0): string {
        const hours = Math.floor(seconds / 3600);
        const minutes = Math.floor((seconds % 3600) / 60);
        const secs = precision > 0 ? (seconds % 60).toFixed(precision) : Math.floor(seconds % 60).toString();

        return `${hours.toString().padStart(2, '0')}:${minutes
            .toString()
            .padStart(2, '0')}:${secs.padStart(precision > 0 ? 6 : 2, '0')}`;
    }
}
